import { useEffect, useState } from 'react';
import { Navbar } from '../../components';
import { useScroll } from 'framer-motion';
import { motion } from 'framer-motion'
import { FADE_BOTTOM } from '../../animations';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Footer from '@/components/sections/Footer';
import SEO from '@/components/SEO';

export async function getStaticProps({ locale }: any) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
      ])),
    },
  }
}

export default function PrivacyPolicy() {
  const [navbarTransparent, setNavbarTransparent] = useState(true)
  const { scrollYProgress, scrollY } = useScroll()
  const { t } = useTranslation();
  const router = useRouter();
  const { asPath: pathname } = router;

  const handleNavigateToTerms = () => {
    const currentLocale = router.locale;
    router.push(`/terms`, undefined, { locale: currentLocale });
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    scrollYProgress.on('change', () => {
      if (scrollY.get() >= 100) {
        setNavbarTransparent(false)
      } else {
        setNavbarTransparent(true)
      }
    })
  }, [])

  const sectionAnimation = {
    initial: FADE_BOTTOM.initial,
    whileInView: FADE_BOTTOM.final,
    transition: { duration: 0.4, delay: 0.3 },
    viewport: { once: true },
  };

  return (
    <>
      <SEO
        title={`DeepNeuronic - ${t("Privacy Policy")}`}
        description={t("policy_p1")}
      />
      <div className="min-h-screen">
        <Navbar transparent={navbarTransparent} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary">
            <div className="absolute inset-0 opacity-20">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-primary rounded-full animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${2 + Math.random() * 3}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="relative z-10 container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center animate-fade-up">
              {/* Badge */}
              <div className="inline-flex items-center justify-center mb-6">
                <div className="relative px-6 py-2 rounded-full bg-electric-blue/10 backdrop-blur-sm light_effect_rounded_full">
                  <span className="text-sm font-semibold tracking-wider uppercase text-electric-blue">
                    {t("Legal")}
                  </span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t("Privacy Policy")}
              </h1>
              <p className="text-xl md:text-2xl text-secondary-foreground leading-relaxed">
                DeepNeuronic
              </p>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-center gap-2">
              <div className="px-6 py-3 text-sm font-semibold text-electric-blue border-b-2 border-electric-blue cursor-default">
                {t("Privacy Policy")}
              </div>
              <div
                onClick={handleNavigateToTerms}
                className="px-6 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground border-b-2 border-transparent hover:border-muted transition-colors cursor-pointer"
              >
                {t("Terms and conditions")}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              {/* Title & Last Updated */}
              <div className="mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Privacy Policy")}</h2>
                <p className="text-muted-foreground">{t("Last updated")}: {t("July")} 13, 2023</p>
              </div>

              {/* Introduction */}
              <motion.div {...sectionAnimation} className="mb-12">
                <p className="text-secondary-foreground leading-relaxed">
                  {t("policy_p1")}
                </p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">
                  {t("policy_p2")}
                </p>
              </motion.div>

              {/* Section: policy_t1 - with list */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t1")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p3")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p4")}</p>
                <ul className="mt-4 space-y-2 text-secondary-foreground leading-relaxed pl-4">
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p4_1")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p4_2")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p4_3")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p4_4")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p4_5")}</li>
                </ul>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p5")}</p>
              </motion.div>

              {/* Section: policy_t2 - with list */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t2")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p6")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p7")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p8")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p9")}</p>
                <ul className="mt-4 space-y-2 text-secondary-foreground leading-relaxed pl-4">
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_1")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_2")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_3")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_4")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_5")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_6")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_7")}</li>
                  <li className="flex gap-2"><span className="text-electric-blue">-</span> {t("policy_p9_8")}</li>
                </ul>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p10")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p11")}</p>
              </motion.div>

              {/* Section: policy_t3 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t3")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p12")}</p>
              </motion.div>

              {/* Section: policy_t4 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t4")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p13")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p14")}</p>
              </motion.div>

              {/* Section: policy_t5 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t5")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p15")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p16")}</p>
              </motion.div>

              {/* Section: policy_t6 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t6")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p17")}</p>
              </motion.div>

              {/* Section: policy_t7 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t7")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p18")}</p>
              </motion.div>

              {/* Section: policy_t8 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t8")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p19")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p20")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p21")}</p>
              </motion.div>

              {/* Section: policy_t9 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t9")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p22")}</p>
              </motion.div>

              {/* Section: policy_t10 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t10")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p23")}</p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">{t("policy_p24")}</p>
              </motion.div>

              {/* Section: policy_t11 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t11")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("policy_p25")}</p>
              </motion.div>

              {/* Section: Contact */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("policy_t12")}</h3>
                <p className="text-secondary-foreground leading-relaxed mb-4">{t("policy_p26")}</p>
                <div className="bg-card border border-border rounded-xl p-6">
                  <p className="text-secondary-foreground">
                    https://www.deepneuronic.com/contact/
                  </p>
                  <p className="text-muted-foreground mt-2">
                    UBIMEDICAL, Estrada Municipal 506, 6200-284 Covilha, Portugal
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
